import css from './index.module.scss';
import { Button, CheckboxLabel, IconNode, ToolTip, Typography } from '@components/base';
import {
  calculateSavedQuoteCMValues,
  getCommaSeperatedCurrencyAmount,
  getFormattedTime,
  sanitizeValue
} from '@helpers/utils';
import { endOfDay, format } from 'date-fns';
import Images from '@assets/images';
import ViewQuoteModal from '../view-quote';
import AddEditQuote from '../add-edit-quote';
import { useEffect, useState } from 'react';
import AccessWrapper from '@authorization/access-wrapper';
import { DeletePrompt, TextField } from '@components/common';
import CpdProductAutocomplete from '@components/common/quote-wrapper/components/cpd-product-autocomplete';
import { getProductDetailsPayload } from '@components/common/quote-wrapper/components/helper';
import { editEnquiryAndProduct } from '@services/quote.service';
import { useParams } from 'react-router-dom';
import notify from '@helpers/toastify-helper';
import { ENQUIRY_TYPE } from '@helpers/constants';
import CMValue from '@components/common/quote-wrapper/components/cm-value';

const NonConsolidationDetail = (props: any) => {
  const { actions } = props;
  const params = useParams();
  const productData = props.productData;
  const { quoteDetail, setQuoteDetail, fetchEnquiryDetail, showQuotes, setShowQuotes } = props;
  const {
    openViewQuoteModal,
    detailModalData,
    openAddEditQuoteModal,
    isConsolidation,
    addEditQuoteModalData,
    addEditQuoteIsEdit,
    isFcl,
    isMtt,
    addEditQuoteIsNew,
    selectedQuotes,
    showDisclaimer,
    primaryUnit,
    enquiryType,
    isAddProduct,
    currency1,
    fxRate
  } = quoteDetail;
  const cm2Context = {
    currency1,
    fxRate,
    primaryUnit,
    isDomestic: enquiryType === ENQUIRY_TYPE.DOMESTIC
  };

  const [editProductState, setEditProductState] = useState<{
    editMode: { [key: number]: boolean };
    updatedProducts: { [key: number]: any };
  }>({
    editMode: {},
    updatedProducts: {}
  });

  useEffect(() => {
    if (productData?.length > 0) {
      const allExpanded = productData.reduce((acc: any, _item: any, index: number) => {
        acc[index] = true;
        return acc;
      }, {});
      setShowQuotes(allExpanded);
    }
  }, [productData, setShowQuotes]);

  const getFormattedDate = (date: any) => {
    if (date) {
      return format(endOfDay(new Date(date).getTime()), 'do LLL');
    } else {
      return null;
    }
  };

  const closeViewQuoteModal = () => {
    setQuoteDetail((prevState: any) => {
      return {
        ...prevState,
        openViewQuoteModal: false
      };
    });
  };

  const isDetailSelected = (detail: any) => {
    return selectedQuotes.some((d: any) => d.quote_id === detail.quote_id);
  };

  const handleDetailCheck = (detail: any) => {
    setQuoteDetail((prevState: any) => {
      return {
        ...prevState,
        selectedQuotes: isDetailSelected(detail)
          ? prevState.selectedQuotes.filter((d: any) => d.quote_id !== detail.quote_id)
          : [...prevState.selectedQuotes, detail]
      };
    });
  };

  const toggleQuotesVisibility = (productIndex: number) => {
    setShowQuotes((prev: any) => ({
      ...prev,
      [productIndex]: !prev[productIndex]
    }));
  };

  const handleAddQuoteClick = (item: any) => {
    setQuoteDetail((prevState: any) => {
      return {
        ...prevState,
        openAddEditQuoteModal: true,
        addEditQuoteModalData: {
          ...item.product
        },
        addEditQuoteIsEdit: false,
        addEditQuoteIsNew: true
      };
    });
  };

  // Function to handle entering edit mode for a specific product
  const handleEditProduct = (index: number, product: any) => {
    setEditProductState((prev) => ({
      editMode: {
        ...prev.editMode,
        [index]: true
      },
      updatedProducts: {
        ...prev.updatedProducts,
        [index]: { ...product }
      }
    }));
  };

  // For when selecting a product from dropdown
  const handleUpdateProduct = (index: number, updatedProduct: any) => {
    setEditProductState((prev) => ({
      ...prev,
      updatedProducts: {
        ...prev.updatedProducts,
        [index]: updatedProduct
      }
    }));
  };

  // Function to save the edited name
  const saveProduct = async (index: number) => {
    const updatedProduct = editProductState.updatedProducts[index];
    const previousProduct = productData[index].product;
    if (!updatedProduct?.product_name) {
      notify({ severity: 'error', message: 'Please select a product' });
      return;
    }
    const reqBody = {
      product_details: {
        old_product_details: {
          id: previousProduct.product_id,
          name: previousProduct.product_name
        },
        // Unchanged → { id, name }; new CPD pick → { cpd_product_id, name } (old id not sent)
        new_product_details: getProductDetailsPayload(updatedProduct)
      }
    };

    const response = await editEnquiryAndProduct(params?.id || '', reqBody);
    if (response.success) {
      notify({
        severity: 'success',
        message: 'Product updated successfully'
      });
      setEditProductState((prev) => ({
        ...prev,
        editMode: {
          ...prev.editMode,
          [index]: false
        },
        updatedProducts: {
          ...prev.updatedProducts,
          [index]: null
        }
      }));
      fetchEnquiryDetail(params?.id || '');
    } else {
      notify({
        severity: 'error',
        message: 'Something went wrong'
      });
      setEditProductState((prev) => ({
        ...prev,
        editMode: {
          ...prev.editMode,
          [index]: true
        }
      }));
    }
  };

  // Function to cancel editing
  const cancelEditProductName = (index: number) => {
    setEditProductState((prev) => ({
      ...prev,
      editMode: {
        ...prev.editMode,
        [index]: false
      }
    }));
  };

  const handleProductSelection = (event: any, param: any, index: number) => {
    handleUpdateProduct(index, param);
  };

  const handleSelectAllQuotes = () => {
    const allQuotes = productData.reduce((acc: any[], item: { details?: any[] }) => {
      return [...acc, ...(item.details || [])];
    }, []);

    const selectedQuoteIds = selectedQuotes.map((quote: { quote_id: number }) => quote.quote_id);

    if (
      allQuotes.length === selectedQuoteIds.length &&
      allQuotes.every((quote: { quote_id: number }) => selectedQuoteIds.includes(quote.quote_id))
    ) {
      setQuoteDetail((prevState: any) => ({
        ...prevState,
        selectedQuotes: []
      }));
    } else {
      setQuoteDetail((prevState: any) => ({
        ...prevState,
        selectedQuotes: allQuotes
      }));
    }
  };

  return (
    <AccessWrapper show={actions?.read}>
      <main className={css.mainWrapper}>
        <Button variant="text" onClick={handleSelectAllQuotes} className={css.selectAllQuotes}>
          {selectedQuotes.length ===
          productData?.reduce((acc: any, item: any) => acc + (item.details?.length || 0), 0)
            ? 'Deselect All Quotes'
            : 'Select All Quotes'}
        </Button>
        {(productData || []).map((item: any, index: number) => {
          const isEditing = editProductState.editMode[index] || false;
          return (
            <div key={index} className={css.productWrapper}>
              <div className={css.header} onClick={() => toggleQuotesVisibility(index)}>
                {!isEditing ? (
                  <Typography
                    variant="h5"
                    className={css.productNameInput}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {item.product.product_name}{' '}
                    <span>
                      <IconNode
                        src={Images.editRed}
                        onClick={() => {
                          handleEditProduct(index, item.product);
                        }}
                      />
                    </span>
                  </Typography>
                ) : (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className={css.autoCompleteProductInput}
                  >
                    <CpdProductAutocomplete
                      required
                      value={editProductState.updatedProducts[index] ?? null}
                      onChange={(product) => handleProductSelection(null, product, index)}
                      rootClassName={css.fieldSpacing2}
                    />
                    <Button
                      variant="text"
                      onClick={(e: any) => {
                        e.stopPropagation();
                        saveProduct(index);
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="text"
                      onClick={(e: any) => {
                        e.stopPropagation();
                        cancelEditProductName(index);
                      }}
                    >
                      Close
                    </Button>
                  </div>
                )}

                <AccessWrapper show={actions?.create}>
                  <div className={css.buttonWrapper}>
                    <Button
                      variant="contained"
                      onClick={(e: any) => {
                        e.stopPropagation();
                        handleAddQuoteClick(item);
                      }}
                    >
                      Add Quote
                    </Button>
                    <ToolTip title={showQuotes[index] ? 'Collapse' : 'Expand'}>
                      <div>
                        <IconNode
                          src={showQuotes[index] ? Images.arrowUpBig : Images.arrowDownBig}
                        />
                      </div>
                    </ToolTip>
                  </div>
                </AccessWrapper>
              </div>
              {showQuotes[index] &&
                (item.details || []).map((detail: any, detailIndex: number) => {
                  const incoTerm = detail?.deal_incoterm;
                  let incoTermLocation = '';

                  if (enquiryType === ENQUIRY_TYPE.DOMESTIC) {
                    incoTermLocation = detail?.deal_incoterm_location;
                  } else {
                    incoTermLocation = incoTerm + ' - ';
                    switch (incoTerm) {
                      case 'EXW':
                      case 'DDP':
                      case 'DAP':
                        incoTermLocation += detail?.deal_incoterm_location;
                        break;

                      case 'FOB':
                        incoTermLocation += detail?.pol?.name;
                        break;

                      case 'CIF':
                      case 'CNF':
                      case 'CFR':
                        incoTermLocation += detail?.pod?.name;
                        break;

                      default:
                        incoTermLocation += detail?.pod?.name;
                        break;
                    }
                  }
                  const detailCMValues = calculateSavedQuoteCMValues(detail, cm2Context);
                  return (
                    <div key={detailIndex} className={css.content}>
                      <div className={css.row}>
                        <div className={css.checkBoxKeyValue}>
                          <div className={css.checkBox}>
                            <CheckboxLabel
                              label=""
                              checked={isDetailSelected(detail)}
                              onChange={() => handleDetailCheck(detail)}
                              className={css.checkboxLabel}
                            />
                            <div className={css.checkedKeyValue}>
                              <div className={css.key}>Quantity</div>
                              <div className={css.value}>
                                {Number(detail?.quantity ?? 0)} {detail?.quantity_unit}
                              </div>
                              <div className={css.dateCaption}>
                                {getFormattedDate(detail.created_at)}{' '}
                                {getFormattedTime(detail.created_at)}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className={css.keyValue}>
                          <div className={css.key}>
                            Quoted Amount{' '}
                            <span>
                              ( Per {detail?.final_price?.unit_of_weight || primaryUnit?.label} )
                            </span>
                          </div>
                          <div className={css.value} style={{ fontWeight: 'bold' }}>
                            {detail?.final_price
                              ? getCommaSeperatedCurrencyAmount(
                                  detail?.final_price?.currency,
                                  detail?.final_price?.value
                                )
                              : '-'}
                          </div>
                        </div>
                        <div className={css.cmKeyValue}>
                          <div className={css.key}>Expected CM1</div>
                          <div className={css.cmValue}>
                            <CMValue
                              amount={detailCMValues.cm1}
                              percent={detailCMValues.cm1Percent}
                              currency={currency1?.label}
                            />
                          </div>
                        </div>
                        <div className={css.cmKeyValue}>
                          <div className={css.key}>Expected CM2</div>
                          <div className={css.cmValue}>
                            <CMValue
                              amount={detailCMValues.cm2}
                              percent={detailCMValues.cm2Percent}
                              currency={currency1?.label}
                            />
                          </div>
                        </div>
                        <div className={css.truncateKeyValue}>
                          <div className={css.key}>
                            {enquiryType === ENQUIRY_TYPE.DOMESTIC
                              ? 'Place of Discharge'
                              : 'Inco Term'}
                          </div>
                          <div className={css.value} title={sanitizeValue(incoTermLocation)}>
                            {sanitizeValue(incoTermLocation)}
                          </div>
                        </div>
                        <div className={css.truncateKeyValue}>
                          <div className={css.key}>Packaging</div>
                          <div className={css.value} title={sanitizeValue(detail?.packaging)}>
                            {sanitizeValue(detail?.packaging)}
                          </div>
                        </div>
                        <div className={css.rowIcons}>
                          <ToolTip title="View Quote" placement="left">
                            <div>
                              <IconNode
                                src={Images.eyeRed}
                                alt="View Quote"
                                className={css.editIcon}
                                onClick={() => {
                                  setQuoteDetail((prevState: any) => {
                                    return {
                                      ...prevState,
                                      openViewQuoteModal: true,
                                      detailModalData: {
                                        ...detail,
                                        ...item.product
                                      }
                                    };
                                  });
                                }}
                              />
                            </div>
                          </ToolTip>
                          <AccessWrapper show={actions?.create}>
                            <ToolTip title="Duplicate Quote" placement="top">
                              <div>
                                <IconNode
                                  src={Images.duplicateRed}
                                  alt="Duplicate icon"
                                  className={css.editIcon}
                                  onClick={() => {
                                    setQuoteDetail((prevState: any) => {
                                      return {
                                        ...prevState,
                                        openAddEditQuoteModal: true,
                                        addEditQuoteModalData: {
                                          ...detail,
                                          ...item.product
                                        },
                                        addEditQuoteIsEdit: false,
                                        isAddProduct: false,
                                        addEditQuoteIsNew: false
                                      };
                                    });
                                  }}
                                />
                              </div>
                            </ToolTip>
                          </AccessWrapper>
                          <AccessWrapper show={actions?.update}>
                            <ToolTip title="Edit Quote" placement="right">
                              <div>
                                <IconNode
                                  src={Images.editRed}
                                  alt="Edit icon"
                                  className={css.editIcon}
                                  onClick={() => {
                                    setQuoteDetail((prevState: any) => {
                                      return {
                                        ...prevState,
                                        openAddEditQuoteModal: true,
                                        addEditQuoteModalData: {
                                          ...detail,
                                          ...item.product
                                        },
                                        addEditQuoteIsEdit: true
                                      };
                                    });
                                  }}
                                />
                              </div>
                            </ToolTip>
                          </AccessWrapper>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          );
        })}
        <AccessWrapper show={actions?.read}>
          {openViewQuoteModal && (
            <ViewQuoteModal
              open={openViewQuoteModal}
              onClose={closeViewQuoteModal}
              data={detailModalData}
              cm2Context={cm2Context}
              isConsolidation={isConsolidation}
              primaryUnit={primaryUnit?.label}
              enquiryType={enquiryType}
            />
          )}
        </AccessWrapper>
        <AccessWrapper show={actions?.create}>
          {openAddEditQuoteModal && (
            <AddEditQuote
              open={openAddEditQuoteModal}
              onClose={() => {
                setQuoteDetail((prevState: any) => {
                  return {
                    ...prevState,
                    showDisclaimer: true
                  };
                });
              }}
              isConsolidation={isConsolidation}
              isFcl={isFcl}
              isMtt={isMtt}
              data={addEditQuoteModalData}
              isEdit={
                addEditQuoteIsNew === true
                  ? 'add'
                  : addEditQuoteIsEdit === true
                  ? 'edit'
                  : isAddProduct === true
                  ? 'addProduct'
                  : 'duplicate'
              }
              quoteDetail={quoteDetail}
              onSubmitClose={() => {
                setQuoteDetail((prevState: any) => {
                  return {
                    ...prevState,
                    openAddEditQuoteModal: false,
                    addEditQuoteIsEdit: false,
                    addEditQuoteIsNew: false
                  };
                });
              }}
              fetchEnquiryDetail={fetchEnquiryDetail}
              enquiryType={enquiryType}
            />
          )}
        </AccessWrapper>

        {showDisclaimer && (
          <DeletePrompt
            open={showDisclaimer}
            onClose={() =>
              setQuoteDetail((prevState: any) => ({
                ...prevState,
                showDisclaimer: false
              }))
            }
            onConfirm={() =>
              setQuoteDetail((prevState: any) => ({
                ...prevState,
                showDisclaimer: false,
                openAddEditQuoteModal: false,
                addEditQuoteIsEdit: false,
                addEditQuoteIsNew: false
              }))
            }
            message="Are you sure you want to close?"
          />
        )}
      </main>
    </AccessWrapper>
  );
};

export default NonConsolidationDetail;
